import { inject, signal, WritableSignal } from "@angular/core";
import { GridApi, GridOptions, GridReadyEvent, CellClassParams, ColDef } from "ag-grid-enterprise";
import { getRowClass, getRowHeight } from "../../tables.utils.js";
import { BudgetView } from "../../../../models/view/budget/budget.view";
import { BudgetUtils } from "../../../../models/view/budget/budget.utils";
import { TableParent } from "@bitwarden/web-vault/app/models/types/table.types.js";
import { BudgetHistoryRow } from "@bitwarden/web-vault/app/models/types/budget.types.js";
import { BudgetHistoryTableSubscription } from "./budget-history-table.subscription";
import { VarianceCellRenderer } from "./renderers/variance.cell-renderer.component";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { DeviceDetectorService } from "ngx-device-detector";
import { SM_BREAKPOINT } from "@bitwarden/web-vault/app/shared/breakpoints";

export class BudgetHistoryTable extends BudgetHistoryTableSubscription {
  gridApi: GridApi;
  options: GridOptions<BudgetHistoryRow>;
  budgetToDetail: WritableSignal<BudgetView> = signal<BudgetView>(null);
  selectedRowIndex: number = -1;
  private deviceDetectorService = inject(DeviceDetectorService);
  private userStore = inject(UserStoreService);
  private readonly mobileCellStyle: { paddingLeft: string; paddingRight: string } | null;

  constructor(parent: TableParent) {
    super();
    this.parent = parent;
    const isMobile =
      this.deviceDetectorService.isMobile() || window.matchMedia(SM_BREAKPOINT).matches;
    this.mobileCellStyle = isMobile ? { paddingLeft: "4px", paddingRight: "4px" } : null;

    const monthColumn: ColDef<BudgetHistoryRow> = {
      field: "month",
      headerName: "Period",
      suppressMovable: true,
      suppressMenu: true,
      sortable: false,
      resizable: false,
      flex: 1,
      headerClass: this.getHeaderClass(),
      cellClass: (params) => this.getMonthCellClass(params),
      cellStyle: () => this.getMobileCellStyle(),
      sort: "desc",
      comparator: this.dateComparator,
    };

    const budgetColumn: ColDef<BudgetHistoryRow> = {
      field: "budget",
      headerName: "Budget",
      suppressMovable: true,
      suppressMenu: true,
      sortable: false,
      resizable: false,
      flex: 1,
      headerClass: this.getHeaderClass(),
      cellClass: (params) => this.getStandardCellClass(params),
      cellStyle: () => this.getMobileCellStyle(),
      valueFormatter: (params) => this.formatCurrency(params.value),
    };

    const columnDefs: ColDef<BudgetHistoryRow>[] = [monthColumn, budgetColumn];

    this.options = {
      columnDefs: [
        ...columnDefs,
        {
          field: "actual",
          headerName: "Actual",
          suppressMovable: true,
          suppressMenu: true,
          sortable: false,
          resizable: false,
          flex: 1,
          headerClass: this.getHeaderClass(),
          cellClass: (params) => this.getStandardCellClass(params),
          cellStyle: () => this.getMobileCellStyle(),
          valueFormatter: (params) =>
            this.formatCurrency(params.value != null ? Math.abs(params.value) : null),
        },
        {
          field: "variance",
          headerName: "Variance",
          suppressMovable: true,
          suppressMenu: true,
          sortable: false,
          resizable: false,
          flex: 1.5,
          headerClass: this.getHeaderClass(),
          cellClass: (params) => this.getVarianceCellClass(params),
          cellStyle: () => this.getMobileCellStyle(),
          cellRenderer: VarianceCellRenderer,
        },
      ],
      context: { parent },
      suppressCellFocus: true,
      suppressDragLeaveHidesColumns: true,
      suppressMovableColumns: true,
      suppressContextMenu: true,
      suppressMenuHide: true,
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
      },
      getRowHeight: getRowHeight,
      getRowClass: (params) => getRowClass(params, this.selectedRowIndex),
    };
  }

  private getMobileCellStyle(): { paddingLeft: string; paddingRight: string } | null {
    return this.mobileCellStyle;
  }

  private getHeaderClass(): string {
    return "tw-text-[13px] sm:tw-text-[12px] tw-font-medium tw-uppercase tw-tracking-[0.04em] tw-text-dt-slate-10 sm:tw-px-1";
  }

  private isLastRow(params: CellClassParams): boolean {
    return params.node.rowIndex === params.api.getDisplayedRowCount() - 1;
  }

  private getBaseCellClass(): string {
    return "tw-leading-[125%] tw-flex tw-items-center tw-border-0";
  }

  private getBorderClass(): string {
    return "tw-border-b tw-border-solid tw-border-b-color-border-subtle tw-shadow-drop-dark2";
  }

  private getMonthCellClass(params: CellClassParams): string {
    const baseClass = "tw-font-medium tw-text-[14px] sm:tw-text-[12px] tw-text-dt-text-headings";
    const commonClass = `${baseClass} ${this.getBaseCellClass()}`;
    return this.isLastRow(params) ? commonClass : `${commonClass} ${this.getBorderClass()}`;
  }

  private getStandardCellClass(params: CellClassParams): string {
    const baseClass =
      "tw-font-D-DIN-PRO tw-font-medium tw-text-[16px] sm:tw-text-[12px] tw-text-dt-text-headings";
    const commonClass = `${baseClass} ${this.getBaseCellClass()}`;
    return this.isLastRow(params) ? commonClass : `${commonClass} ${this.getBorderClass()}`;
  }

  private getVarianceCellClass(params: CellClassParams): string {
    return this.isLastRow(params) ? "tw-border-0" : `tw-border-0 ${this.getBorderClass()}`;
  }

  private formatCurrency(value: number | null): string {
    // Rows are already in the budget's own currency (see subscription), so label with it too.
    const code =
      this.getSelectedBudget()?.currency ||
      this.userStore.preferences.preferenceView()?.coreCurrency;
    return BudgetUtils.formatCurrency(value, code);
  }

  private dateComparator(date1: string, date2: string): number {
    if (!date1 || !date2) {
      return 0;
    }

    const d1 = new Date(date1);
    const d2 = new Date(date2);

    return d1.getTime() - d2.getTime();
  }
}
